// Integration health: one set of rules for the Integrations page, the Overview
// status line and the monitoring banner on every page. Pure, so it is checked
// by integrations.check.ts.
//
// The point of the model is to keep three different problems apart:
//   collector  - our job itself failed (crashed, DB error, misconfigured)
//   source     - the job ran but OLTs/devices did not answer (a NETWORK signal)
//   external   - an external system (GenieACS, LibreNMS, WHMCS) errored
// and to never call an integration healthy because its LAST run was OK when
// that run is old relative to the interval it is supposed to run at.

export type RunMetrics = {
  total: number;
  failed: number;
  counts?: Record<string, number>;
  failures?: string[];
  warnings?: string[];
};

export type IntegrationRow = {
  name: string;
  last_run: { id?: number; status: string; detail?: string | null; started_at?: string; finished_at?: string; metrics?: RunMetrics | null };
  last_ok_at: string | null;
  last_failure: { id: number; finished_at: string; detail: string | null } | null;
  interval_s: number;
  running_since: string | null;
  /** Real progress of the run in flight, when the job can count it. */
  progress?: { stage: string; done: number; total: number } | null;
  endpoint: string;
  stats: {
    runs_1h: number; ok_1h: number; runs_24h: number; ok_24h: number;
    avg_1h_ms: number | null; p50_24h_ms: number | null; p95_24h_ms: number | null;
    recent: { s: string; ms: number; at: string }[];
  };
};

export type IntState = "healthy" | "running" | "degraded" | "failed" | "stale" | "disabled" | "unknown";
/** Why a non-healthy state was reached; drives the explanation and the problem tag. */
export type Cause = "collector" | "external" | "source" | "partial" | "warnings" | "slow" | "stale" | "never" | null;
export type Kind = "olt" | "snmp" | "librenms" | "genieacs" | "whmcs" | "bras" | "other";
export type Group = "collectors" | "observability" | "acs" | "billing" | "other";

export const kindOf = (name: string): Kind =>
  name.startsWith("genieacs:") ? "genieacs" : (["olt", "snmp", "librenms", "whmcs", "bras"].includes(name) ? name as Kind : "other");

/** "OLT Collector", "GenieACS · Athanet": how an integration is named on screen. */
export const displayName = (name: string) => {
  const k = kindOf(name);
  if (k === "genieacs") return `GenieACS · ${name.slice("genieacs:".length)}`;
  return ({ olt: "OLT Collector", snmp: "SNMP", librenms: "LibreNMS", whmcs: "WHMCS", bras: "BRAS", other: name } as Record<Kind, string>)[k];
};

/** Our own pollers talk to network devices; the rest mirror an external system. */
export const isCollector = (k: Kind) => k === "olt" || k === "snmp" || k === "bras";

export const GROUP: Record<Kind, Group> = { olt: "collectors", snmp: "collectors", librenms: "observability", genieacs: "acs", whmcs: "billing", bras: "collectors", other: "other" };

/** What each integration feeds in JALABYTE OPS: the impact list when it breaks. */
export const PROVIDES: Record<Kind, string[]> = {
  olt: ["ontStatus", "optical", "ponHealth", "oltHealth", "outages"],
  snmp: ["oltReach"],
  librenms: ["devices", "links", "traffic"],
  genieacs: ["cpe", "wan", "tr069"],
  whmcs: ["subscribers", "billing"],
  bras: ["bras", "internet"],
  other: [],
};

/** Collectors whose staleness makes the network picture itself untrustworthy. */
export const CRITICAL: Kind[] = ["olt", "snmp"];

// Used only until the worker has stamped the real interval (first run after deploy).
const DEFAULT_INTERVAL_S: Record<Kind, number> = { olt: 120, snmp: 60, librenms: 300, genieacs: 300, whmcs: 900, bras: 60, other: 300 };

export type Health = {
  state: IntState;
  cause: Cause;
  running: boolean;
  intervalMs: number;
  /** Stale after this long without a success: two missed runs plus grace. */
  windowMs: number;
  /** Since the last successful run; null = never succeeded (in view). */
  sinceOkMs: number | null;
  lastMs: number | null;
};

export function integrationHealth(r: IntegrationRow, now: number): Health {
  const kind = kindOf(r.name);
  const intervalMs = (r.interval_s || DEFAULT_INTERVAL_S[kind]) * 1000;
  const windowMs = 2 * intervalMs + 30_000;
  const runAge = r.running_since ? now - Date.parse(r.running_since) : null;
  // A crashed run leaves its stamp behind: past this age it is not "running".
  const running = runAge !== null && runAge < Math.max(3 * intervalMs, 10 * 60_000);
  const sinceOkMs = r.last_ok_at ? now - Date.parse(r.last_ok_at) : null;
  const lr = r.last_run;
  const lastMs = lr.started_at && lr.finished_at ? Date.parse(lr.finished_at) - Date.parse(lr.started_at) : null;
  const h = (state: IntState, cause: Cause): Health => ({ state, cause, running, intervalMs, windowMs, sinceOkMs, lastMs });

  if (lr.status === "unknown") return h(running ? "running" : "unknown", "never");
  if (lr.status === "disabled") return h("disabled", null);
  const m = lr.metrics;
  if (lr.status === "error") {
    if (m && m.total > 0 && m.failed >= m.total) return h("failed", "source");
    return h("failed", isCollector(kind) ? "collector" : "external");
  }
  if (sinceOkMs === null || sinceOkMs > windowMs) return h("stale", "stale");
  if (m && m.failed > 0) return h("degraded", "partial");
  if (m?.warnings?.length) return h("degraded", "warnings");
  const p50 = r.stats.p50_24h_ms;
  if (lastMs !== null && p50 !== null && r.stats.runs_24h >= 10 && lastMs > Math.max(3 * p50, p50 + 30_000)) return h("degraded", "slow");
  return h(running ? "running" : "healthy", null);
}

/** Worst first; the order problem cards are pulled to the top in. */
export const STATE_RANK: Record<IntState, number> = { failed: 0, stale: 1, degraded: 2, running: 3, healthy: 4, unknown: 5, disabled: 6 };

export type Posture = "operational" | "degraded" | "failure" | "unknown";

export function posture(hs: Health[]): { posture: Posture; counts: Record<IntState, number> } {
  const counts = { healthy: 0, running: 0, degraded: 0, failed: 0, stale: 0, disabled: 0, unknown: 0 } as Record<IntState, number>;
  for (const x of hs) counts[x.state]++;
  const p: Posture = counts.failed ? "failure"
    : counts.stale || counts.degraded ? "degraded"
    : counts.healthy + counts.running ? "operational" : "unknown";
  return { posture: p, counts };
}

/**
 * Can the network picture be trusted? Descriptive, never a score. Only the
 * critical collectors move the level; the rest add side notes (CPE data,
 * billing) that do not change what the NOC knows about the network.
 */
export type Note = { key: string; params: Record<string, string | number>; name: string; state: IntState };
export type Confidence = { level: "good" | "degraded" | "low"; notes: Note[]; side: Note[] };

export function confidence(rows: IntegrationRow[], now: number): Confidence {
  const notes: Note[] = [];
  const side: Note[] = [];
  let level: Confidence["level"] = "good";
  const worse = (l: Confidence["level"]) => { if (l === "low" || level === "good") level = l; };
  const byKind = (k: Kind) => rows.find((r) => kindOf(r.name) === k);

  const olt = byKind("olt");
  const oh = olt && integrationHealth(olt, now);
  if (!olt || !oh || oh.state === "unknown") {
    worse("degraded");
    notes.push({ key: "conf.oltNever", params: {}, name: "olt", state: "unknown" });
  } else if (oh.state === "failed" || oh.state === "stale") {
    worse("low");
    notes.push({ key: oh.sinceOkMs === null ? "conf.oltDownNever" : "conf.oltDown", params: { age: oh.sinceOkMs ?? 0 }, name: olt.name, state: oh.state });
  } else if (oh.cause === "partial") {
    const m = olt.last_run.metrics!;
    worse("degraded");
    notes.push({ key: "conf.oltPartial", params: { n: m.failed, total: m.total }, name: olt.name, state: oh.state });
  }

  const snmp = byKind("snmp");
  const sh = snmp && integrationHealth(snmp, now);
  if (snmp && sh && (sh.state === "failed" || sh.state === "stale")) {
    worse("degraded");
    const oltOk = oh && (oh.state === "healthy" || oh.state === "running");
    notes.push({ key: oltOk ? "conf.snmpDownOltOk" : "conf.snmpDown", params: { age: sh.sinceOkMs ?? 0 }, name: snmp.name, state: sh.state });
  }

  for (const r of rows) {
    const k = kindOf(r.name);
    if (CRITICAL.includes(k)) continue;
    const x = integrationHealth(r, now);
    if (x.state === "failed" || x.state === "stale") side.push({ key: `conf.side.${k}`, params: { age: x.sinceOkMs ?? 0 }, name: r.name, state: x.state });
  }
  return { level, notes, side };
}

/** "2.4s", "48s", "1m 12s": run durations. */
export function msText(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60_000)}m ${String(Math.round((ms % 60_000) / 1000)).padStart(2, "0")}s`;
}

/** "every 2m", "every 15m", "every 1h". */
export function everyText(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
}
